<template>
  <div>
    <vx-card class="mb-4">
      <validation-observer ref="validator" #default="{valid}">
        <b-form
          :class="[inline ? 'gap-2 items-center' : '']"
          :inline="inline"
          @submit.prevent="createOrUpdate"
          @reset.prevent="clearForm">

          <validation-provider
            #default="validationContext"
            :name="$t('subscription.user')"
            rules="required"
            class="flex-grow"
            slim>
            <select-suggestion
              id="user_id"
              column="name,email"
              model="User"
              v-model="user"
              :class="user ? 'flex-grow' : 'flex-grow required'"
              :label="$t('subscription.user')"
              :appendClearOption="!isEdit"
              :isDisabled="isEdit"
              :itemTextFormat="formatUserNameEmailHtml"
              dropdown-options-class="user-suggest-dropdown"
              placeholderText="Digite o nome ou e-mail do usuário"
              :state="getValidationState(validationContext)"
            />
            <b-form-invalid-feedback :class="[{'absolute': inline}]">
              {{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('subscription.plan')"
            rules="required"
            class="flex-grow mt-2"
            slim>
            <b-form-group
              :label="$t('subscription.plan')"
              label-for="subscription_plan_id">
              <v-select
                id="subscription_plan_id"
                v-model="model.subscription_plan_id"
                :options="planOptions"
                label="name"
                :reduce="opt => opt.id"
                class="required-select"
                :required="true"
                :disabled="isEdit">
                <div slot="no-options">{{ $t('subscription.no_plans') }}</div>
              </v-select>
              <b-form-invalid-feedback :class="[{'absolute': inline}]">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            :name="$t('subscription.status')"
            rules="required"
            class="flex-grow"
            slim>
            <b-form-group
              :label="$t('subscription.status')"
              label-for="status">
              <v-select
                id="status"
                v-model="model.status"
                :options="statusOptions"
                label="label"
                :reduce="opt => opt.value"
                class="required-select"
                :required="true">
                <div slot="no-options">{{ $t('subscription.no_statuses') }}</div>
              </v-select>
              <b-form-invalid-feedback :class="[{'absolute': inline}]">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div v-if="!isEdit && requiresPagBankCard" class="vx-row w-full">
            <div class="vx-col w-full mb-2">
              <p class="text-sm text-grey">
                {{ $t('subscription.manual_gateway_help') }}
              </p>
            </div>
            <div v-if="!user || !user.id" class="vx-col w-full mb-2">
              <p class="text-sm text-warning">
                {{ $t('subscription.manual_gateway_select_user_first') }}
              </p>
            </div>
            <div v-else-if="loadingUserCards" class="vx-col w-full mb-2">
              <p class="text-sm text-grey">{{ $t('common.loading') }}</p>
            </div>
            <div v-else-if="!pagBankEligibleCards.length" class="vx-col w-full mb-2">
              <p class="text-sm text-warning">
                {{ $t('subscription.manual_gateway_no_pagbank_cards') }}
              </p>
            </div>
            <div v-else class="vx-col sm:w-6/12 w-full">
              <validation-provider
                #default="validationContext"
                :name="$t('subscription.credit_card')"
                rules="required"
                slim>
                <b-form-group
                  :label="$t('subscription.credit_card')"
                  label-for="credit_card_id">
                  <v-select
                    id="credit_card_id"
                    v-model="model.credit_card_id"
                    :options="pagBankEligibleCards"
                    label="card_label"
                    :reduce="opt => opt.id"
                    class="required-select w-full"
                    :placeholder="$t('subscription.credit_card_placeholder')"
                    :clearable="true">
                    <div slot="no-options">{{ $t('subscription.manual_gateway_no_pagbank_cards') }}</div>
                  </v-select>
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>
            <div v-if="!isEdit && requiresPagBankCard && user && user.id && pagBankEligibleCards.length" class="vx-col sm:w-6/12 w-full">
              <validation-provider
                #default="validationContext"
                :name="$t('subscription.card_security_code')"
                rules="required"
                slim>
                <b-form-group
                  :label="$t('subscription.card_security_code')"
                  label-for="card_security_code">
                  <b-form-input
                    id="card_security_code"
                    v-model="model.card_security_code"
                    type="password"
                    autocomplete="off"
                    class="w-full"
                    :state="getValidationState(validationContext)"/>
                  <b-form-invalid-feedback :state="getValidationState(validationContext)">
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>
          </div>

          <div class="vx-row">
            <div class="vx-col sm:w-6/12 w-full">
              <b-form-group
                :label="$t('subscription.started_at')"
                label-for="started_at">
                <b-form-input
                  id="started_at"
                  type="datetime-local"
                  v-model="model.started_at"
                  class="w-full"/>
              </b-form-group>
            </div>
            <div class="vx-col sm:w-6/12 w-full">
              <b-form-group
                :label="$t('subscription.ends_at')"
                label-for="ends_at">
                <b-form-input
                  id="ends_at"
                  type="datetime-local"
                  v-model="model.ends_at"
                  class="w-full"/>
              </b-form-group>
            </div>
          </div>

          <b-form-row :class="['justify-end mt-2', inline ? 'ml-auto' : '' ]">
            <b-col cols="auto">
              <b-button :disabled="!valid || subscriptionPagBankIncomplete" type="submit" variant="primary">
                {{ $t('subscription_plan.save') }}
              </b-button>
            </b-col>
            <b-col cols="auto">
              <b-button type="reset" variant="outline-secondary">
                {{ $t('subscription_plan.reset') }}
              </b-button>
            </b-col>
            <b-col cols="auto">
              <b-button
                variant="outline-secondary"
                class="float-right"
                @click="goBack">
                <feather-icon
                  icon="CornerUpLeftIcon"
                  class="mr-50"/>
                <span class="align-middle">{{ $t('back') }}</span>
              </b-button>
            </b-col>
          </b-form-row>
        </b-form>
      </validation-observer>
    </vx-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BFormRow,
  BCol,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import { formatUserNameEmailHtml } from '@/util/suggestionLabelFormat'

import SubscriptionService from '@/services/api/SubscriptionService'
import UserCreditCardService from '@/services/api/UserCreditCardService'

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BFormRow,
    BCol,
    BFormInvalidFeedback,
    ValidationObserver,
    ValidationProvider,
    vSelect,
    SelectSuggestion,
  },

  props: {
    id: {
      type: [Number, String],
      default: null
    },
    showSubmit: {
      type: Boolean,
      default: true
    },
    showReset: {
      type: Boolean,
      default: true
    },
    inline: {
      type: Boolean,
      default: false
    },
  },

  data: () => ({
    model: {
      id: null,
      user_id: null,
      subscription_plan_id: null,
      status: 'PE',
      started_at: null,
      ends_at: null,
      credit_card_id: null,
      card_security_code: null,
    },
    user: null,
    planOptions: [],
    statusOptions: [],
    userCards: [],
    loadingUserCards: false,
  }),

  computed: {
    isEdit() {
      return !_.isNil(this.id)
    },
    selectedPlan() {
      const pid = this.model.subscription_plan_id
      if (!pid || !this.planOptions || !this.planOptions.length) return null
      return this.planOptions.find(p => p.id === pid) || null
    },
    requiresPagBankCard() {
      return !!(this.selectedPlan && this.selectedPlan.register_on_payment_gateway)
    },
    /** Cartões tokenizados no PagBank (mesmo critério do backend para assinatura manual). */
    pagBankEligibleCards() {
      return (this.userCards || [])
        .filter(c => {
          const g = String(c.gateway || '').toLowerCase()
          return g === 'pagbank' && !!c.gateway_card_id
        })
        .map(c => ({
          ...c,
          card_label: `${c.masked_number || '****'} · ${c.brand_label || c.brand || ''} · #${c.id}`
        }))
    },
    /** Impede envio sem cartão/CVV quando o plano exige PagBank. */
    subscriptionPagBankIncomplete() {
      if (this.isEdit || !this.requiresPagBankCard) return false
      if (!this.user || !this.user.id) return true
      if (this.loadingUserCards) return true
      if (!this.pagBankEligibleCards.length) return true
      return (
        this.model.credit_card_id == null ||
        !String(this.model.card_security_code || '').trim()
      )
    }
  },

  watch: {
    user(newVal, oldVal) {
      if (this.isEdit) return
      const newId = newVal && newVal.id
      const oldId = oldVal && oldVal.id
      if (newId !== oldId) {
        this.model.credit_card_id = null
        this.model.card_security_code = null
        this.userCards = []
        if (newId) {
          this.fetchUserCards(newId)
        }
      }
    }
  },

  methods: {
    formatUserNameEmailHtml,
    fetchUserCards(userId) {
      this.loadingUserCards = true
      const svc = UserCreditCardService.build(this.$vs)
      svc
        .list(userId)
        .then(rows => {
          this.userCards = Array.isArray(rows) ? rows : []
        })
        .catch(() => {
          this.userCards = []
        })
        .finally(() => {
          this.loadingUserCards = false
        })
    },
    loadData(id) {
      this.$vs.loading()
      this.service.read(id).then(
        response => {
          this.model.id = response.id
          this.model.user_id = response.user_id
          this.model.subscription_plan_id = response.subscription_plan_id
          this.model.status = response.status
          this.model.started_at = this.toDatetimeLocal(response.started_at)
          this.model.ends_at = this.toDatetimeLocal(response.ends_at)
          this.user = response.user
        },
        error => { this.$vs.loading.close() }
      ).finally(() => { this.$vs.loading.close() })
    },
    toDatetimeLocal(val) {
      if (!val) return null
      const d = new Date(val)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    fromDatetimeLocal(val) {
      if (!val) return null
      return new Date(val).toISOString()
    },
    createOrUpdate() {
      this.$vs.loading()

      const modelData = {
        id: this.id,
        status: this.model.status,
        started_at: this.fromDatetimeLocal(this.model.started_at),
        ends_at: this.fromDatetimeLocal(this.model.ends_at),
      }
      if (!this.isEdit) {
        modelData.user_id = this.user ? this.user.id : this.model.user_id
        modelData.subscription_plan_id = this.model.subscription_plan_id
      }
      if (!this.isEdit && this.requiresPagBankCard) {
        modelData.credit_card_id = this.model.credit_card_id
        modelData.card_security_code = this.model.card_security_code
      }

      this.service.createOrUpdate(modelData).then(
        data => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          if (data && data.id) {
            this.$router.push(`/subscriptions/${data.id}/edit`)
          } else {
            this.$router.push('/subscriptions')
          }
        },
        error => {
          this.showErrors(this.$refs.validator, error, this)
        }
      ).finally(() => { this.$vs.loading.close() })
    },
    clearForm() {
      this.clearValidationErrors()
      this.$emit('reset')
      this.model = {
        id: null,
        user_id: null,
        subscription_plan_id: null,
        status: 'PE',
        started_at: null,
        ends_at: null,
        credit_card_id: null,
        card_security_code: null,
      }
      this.user = null
      this.userCards = []
    },
    goBack() {
      this.$router.push('/subscriptions')
    },
    getValidationState({ invalid, validated }) {
      const valid = !invalid
      return !validated ? null : valid
    },
    clearValidationErrors() {
      this.$refs.validator.reset()
    }
  },

  beforeMount() {
    this.service = SubscriptionService.build(this.$vs)
  },

  mounted() {
    if (this.id) {
      this.loadData(this.id)
    }

    this.$vs.loading()
    Promise.all([
      this.service.findAllPlans(),
      this.service.findAllStatuses(),
    ]).then(([plans, statuses]) => {
      this.planOptions = plans
      this.statusOptions = statuses
    }).finally(() => {
      this.$vs.loading.close()
    })
  }
}
</script>

<style lang="scss">
  .inline-input-group > div:nth-child(2) {
    width: 100%;
  }
</style>
